import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

export default function ChangePassword() {
  const { user, refresh, loginRedirectPath } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await refresh();
      nav(loginRedirectPath(user), { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-[#FAFAFA]">
      <div className="max-w-md w-full border co-hairline bg-white p-8">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="h-5 w-5 text-[#4338CA]" strokeWidth={1.75} />
          <span className="font-heading font-bold tracking-tight">CollectOne</span>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">
          {user?.must_change_password ? "Set a new password" : "Change password"}
        </h1>
        <p className="text-sm text-[#52525B] mt-2">
          {user?.must_change_password
            ? "Your account was created with a temporary password. Please set a new one to continue."
            : "Keep your account secure with a fresh password."}
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label className="co-label">Current password</Label>
            <Input type="password" data-testid="cp-current" value={current} onChange={(e) => setCurrent(e.target.value)} required className="mt-2 h-11" />
          </div>
          <div>
            <Label className="co-label">New password</Label>
            <Input type="password" data-testid="cp-new" value={next} onChange={(e) => setNext(e.target.value)} minLength={8} required className="mt-2 h-11" placeholder="At least 8 characters" />
          </div>
          <div>
            <Label className="co-label">Confirm new password</Label>
            <Input type="password" data-testid="cp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={8} required className="mt-2 h-11" />
          </div>
          <button
            type="submit" disabled={busy}
            data-testid="cp-submit"
            className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white font-semibold py-3 rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200"
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
